<script lang="ts">
  import LoadingSpinner from '../../ui/LoadingSpinner.svelte';
  import type { PlanId, Cadence, Plans } from './types';

  interface Props {
    selectedPlan: PlanId;
    cadence: Cadence;
    plans: Plans;
    isSubmitting: boolean;
    errorMessage: string;
    onPaymentSubmit: (e: Event) => void;
    onSkipPayment: () => void;
    onBack: () => void;
  }

  let {
    selectedPlan,
    cadence,
    plans,
    isSubmitting,
    errorMessage,
    onPaymentSubmit,
    onSkipPayment,
    onBack,
  }: Props = $props();

  let plan = $derived(plans[selectedPlan]);
  let displayPrice = $derived(cadence === 'annual' ? plan.annual : plan.monthly);
</script>

<div class="signup-form">
  <div class="form-section">
    <h3>Payment Details</h3>
    <p class="payment-intro">
      Your 14-day free trial starts immediately. Add your payment details now, or skip and add them later from your dashboard.
    </p>
  </div>

  <div class="payment-summary">
    <div class="summary-row">
      <span>Plan</span>
      <strong>{plan.name}</strong>
    </div>
    <div class="summary-row">
      <span>Billing</span>
      <strong>{cadence === 'annual' ? 'Annual' : 'Monthly'}</strong>
    </div>
    <div class="summary-row">
      <span>Price after trial</span>
      <strong>{displayPrice}/mo</strong>
    </div>
    <div class="summary-row highlight">
      <span>Today's charge</span>
      <strong>$0.00</strong>
    </div>
  </div>

  {#if errorMessage}
    <div class="error-banner">
      <span>!</span>
      {errorMessage}
    </div>
  {/if}

  <div class="payment-options">
    <button
      type="button"
      class="btn-primary payment-btn"
      onclick={onPaymentSubmit}
      disabled={isSubmitting}
    >
      {#if isSubmitting}
        <LoadingSpinner size="small" light />
        Redirecting to Stripe...
      {:else}
        <svg class="btn-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <rect x="1" y="4" width="22" height="16" rx="2" ry="2"/>
          <line x1="1" y1="10" x2="23" y2="10"/>
        </svg>
        Add Payment Details
      {/if}
    </button>

    <div class="or-divider">
      <span>or</span>
    </div>

    <button
      type="button"
      class="btn-secondary skip-btn"
      onclick={onSkipPayment}
      disabled={isSubmitting}
    >
      {#if isSubmitting}
        <LoadingSpinner size="small" light />
        Starting trial...
      {:else}
        Skip for now — I'll add payment later
      {/if}
    </button>
  </div>

  <div class="form-actions">
    <button
      type="button"
      class="btn-text"
      onclick={onBack}
      disabled={isSubmitting}
    >
      ← Back to plan selection
    </button>
  </div>

  <p class="helper">
    <svg class="lock-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
      <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
      <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
    </svg>
    Secure payments powered by Stripe. Cancel anytime during your trial.
  </p>
</div>

<style>
  .signup-form {
    background: white;
    border-radius: 16px;
    padding: clamp(1.5rem, 3vw, 2.5rem);
    box-shadow: 0 20px 60px rgba(0, 0, 0, 0.12);
  }

  .form-section {
    margin-bottom: 2rem;
  }

  .form-section h3 {
    color: #1f2937;
    font-size: 1.1rem;
    font-weight: 600;
    margin-bottom: 1rem;
    padding-bottom: 0.5rem;
    border-bottom: 1px solid #e5e7eb;
  }

  .payment-intro {
    color: var(--color-gray-500);
    font-size: 1rem;
    line-height: 1.6;
    margin: 0;
  }

  .payment-summary {
    background: var(--color-gray-50);
    border: 1px solid var(--color-gray-200);
    border-radius: var(--radius-lg);
    padding: 1.25rem;
    margin-bottom: 1.5rem;
  }

  .summary-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 0.625rem 0;
    border-bottom: 1px solid var(--color-gray-200);
  }

  .summary-row:last-child {
    border-bottom: none;
  }

  .summary-row span {
    color: var(--color-gray-500);
    font-size: 0.95rem;
  }

  .summary-row strong {
    color: var(--color-gray-800);
    font-size: 0.95rem;
  }

  .summary-row.highlight {
    background: rgba(16, 185, 129, 0.08);
    margin: 0.5rem -1.25rem -1.25rem;
    padding: 1rem 1.25rem;
    border-radius: 0 0 12px 12px;
  }

  .summary-row.highlight span,
  .summary-row.highlight strong {
    color: var(--color-success);
    font-weight: 600;
  }

  .error-banner {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    padding: 0.875rem 1rem;
    background: var(--color-error-bg);
    border: 1px solid #fecaca;
    color: var(--color-error);
    border-radius: 10px;
    margin-bottom: 1.5rem;
    font-weight: 500;
    font-size: 0.9rem;
    animation: shake 0.5s ease-in-out;
  }

  @keyframes shake {
    0%, 100% { transform: translateX(0); }
    20%, 60% { transform: translateX(-5px); }
    40%, 80% { transform: translateX(5px); }
  }

  .error-banner span {
    width: 1.25rem;
    height: 1.25rem;
    border-radius: 50%;
    background: var(--color-error);
    color: white;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 0.75rem;
    font-weight: 700;
    flex-shrink: 0;
  }

  .payment-options {
    display: flex;
    flex-direction: column;
    gap: 1rem;
    margin-bottom: 1.5rem;
  }

  .payment-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.625rem;
  }

  .btn-icon {
    width: 1.25rem;
    height: 1.25rem;
  }

  .or-divider {
    display: flex;
    align-items: center;
    gap: 1rem;
    color: #9ca3af;
    font-size: 0.85rem;
    text-transform: uppercase;
    letter-spacing: 0.1em;
  }

  .or-divider::before,
  .or-divider::after {
    content: '';
    flex: 1;
    height: 1px;
    background: #e5e7eb;
  }

  .btn-primary {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 0.5rem;
    padding: 0.9rem 1.5rem;
    background: linear-gradient(135deg, #4A7DFF 0%, #6B5CE7 100%);
    color: white;
    border: none;
    border-radius: 10px;
    font-size: 1rem;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s ease;
  }

  .btn-primary:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 8px 20px rgba(74, 125, 255, 0.35);
  }

  .btn-primary:disabled {
    opacity: 0.7;
    cursor: not-allowed;
  }

  .btn-secondary {
    padding: 0.9rem 1.5rem;
    background: white;
    color: var(--color-gray-700);
    border: 2px solid var(--color-gray-200);
    border-radius: 10px;
    font-size: 1rem;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s ease;
  }

  .btn-secondary:hover:not(:disabled) {
    border-color: #4A7DFF;
    color: #4A7DFF;
  }

  .btn-secondary:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  .skip-btn {
    font-size: 0.95rem;
    padding: 0.875rem 1.25rem;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.5rem;
  }

  .form-actions {
    margin-top: 1.5rem;
  }

  .btn-text {
    background: none;
    border: none;
    color: #6b7280;
    font-size: 0.9rem;
    cursor: pointer;
    padding: 0.5rem;
    transition: color 0.2s ease;
  }

  .btn-text:hover:not(:disabled) {
    color: #4A7DFF;
  }

  .btn-text:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  .helper {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.5rem;
    text-align: center;
    color: var(--color-gray-400);
    font-size: 0.85rem;
    margin-top: 1rem;
  }

  .lock-icon {
    width: 1rem;
    height: 1rem;
    color: #9ca3af;
  }

  @media (max-width: 640px) {
    .payment-summary {
      padding: 1rem;
    }

    .summary-row.highlight {
      margin: 0.5rem -1rem -1rem;
      padding: 0.875rem 1rem;
    }
  }
</style>
